import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { bookService } from "../services/bookService";
import { readingProgressService } from "../services/readingProgressService";
import { libraryOrganizationService } from "../services/libraryOrganizationService";
import { libraryViewService } from "../services/libraryViewService";
import { libraryLocationService } from "../services/libraryLocationService";
import { readingGoalService } from "../services/readingGoalService";
import { journalService, type JournalFilters } from "../services/journalService";
import { readingSessionService } from "../services/readingSessionService";
import { readingCycleService } from "../services/readingCycleService";
import { statsService } from "../services/statsService";
import { nextBookService } from "../services/nextBookService";
import {
  Book,
  BookPage,
  BookCreate,
  BookUpdate,
  BookAbandon,
  BookResume,
  ReadingProgress,
  ReadingProgressCreate,
  ReadingGoalCreate,
  ReadingGoalUpdate,
  ReadingSessionCreate,
  ReadingSession,
  ReadingSessionUpdate,
  CollectionCreate,
  EntryOrganizationUpdate,
  TagCreate,
  JournalEntry,
  JournalEntryCreate,
  JournalEntryUpdate,
  SavedLibraryViewCreate,
  SavedLibraryViewUpdate,
  NextBookConstraints,
  SeriesCorrectionUpdate,
  BulkLibraryOperation,
  JournalEntryList,
  ReadingGoal,
  SavedLibraryView,
  LibraryCollection,
  LibraryTag,
  LibraryLocation,
  LibraryLocationInput,
  ReflectionPrompt,
} from "../types";
import { versionEtag } from "../services/concurrency";
import {
  BookListFilters,
  canonicalBookFilters,
  queryKeys,
  SessionListParams,
} from "./keys";

const BOOK_STALE_TIME = 30_000;
const STATS_STALE_TIME = 60_000;

export function useChooseNextBookMutation() {
  return useMutation({
    mutationFn: (payload: NextBookConstraints) => nextBookService.choose(payload),
  });
}

export function useBooksQuery(filters: BookListFilters = {}, enabled = true) {
  const canonical = canonicalBookFilters(filters);
  return useQuery({
    queryKey: queryKeys.books.list(canonical),
    queryFn: ({ signal }) => bookService.getAllBooks(canonical, signal),
    enabled,
    staleTime: BOOK_STALE_TIME,
  });
}

export function useBookQuery(bookId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.books.detail(bookId ?? 0),
    queryFn: ({ signal }) => bookService.getBookById(bookId as number, signal),
    enabled: bookId !== undefined && Number.isInteger(bookId) && bookId > 0,
    staleTime: BOOK_STALE_TIME,
  });
}

export function useBookSeriesQuery(bookId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.books.series(bookId ?? 0),
    queryFn: ({ signal }) => bookService.getBookSeries(bookId as number, signal),
    enabled: bookId !== undefined && Number.isInteger(bookId) && bookId > 0,
    retry: false,
    staleTime: BOOK_STALE_TIME,
  });
}

export function useEditionProgressQuery(bookId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.books.editionProgress(bookId ?? 0),
    queryFn: ({ signal }) => bookService.getEditionProgress(bookId as number, signal),
    enabled: bookId !== undefined && Number.isInteger(bookId) && bookId > 0,
    retry: false,
    staleTime: BOOK_STALE_TIME,
  });
}

export function useArchivedBooksQuery(enabled = true) {
  return useQuery({
    queryKey: queryKeys.books.archived(),
    queryFn: ({ signal }) => bookService.getArchivedBooks(signal),
    enabled,
    staleTime: BOOK_STALE_TIME,
  });
}

export function useUpdateBookSeriesMutation(bookId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (update: SeriesCorrectionUpdate) =>
      bookService.updateBookSeries(bookId, update),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.books.series(bookId) });
    },
  });
}

export function useBulkLibraryMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: BulkLibraryOperation) => {
      const books = client
        .getQueriesData<BookPage>({ queryKey: queryKeys.books.lists() })
        .flatMap(([, page]) => page?.items ?? []);
      const expectedVersions = Object.fromEntries(
        payload.entry_ids.flatMap((id) => {
          const tag = versionEtag(books.find((book) => book.id === id)?.updated_at);
          return tag ? [[id, tag]] : [];
        })
      );
      return bookService.bulkUpdate({
        ...payload,
        ...(Object.keys(expectedVersions).length === payload.entry_ids.length
          ? { expected_versions: expectedVersions }
          : {}),
      });
    },
    onSuccess: async (result) => {
      result.items.forEach((book) => {
        client.setQueryData(queryKeys.books.detail(book.id), book);
      });
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.books.all() }),
        client.invalidateQueries({ queryKey: queryKeys.organization.all() }),
        client.invalidateQueries({ queryKey: queryKeys.stats.all() }),
        client.invalidateQueries({ queryKey: queryKeys.goals.all() }),
        client.invalidateQueries({ queryKey: queryKeys.books.undoHistory() }),
      ]);
    },
  });
}

export function useUndoLibraryMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => bookService.undo(token),
    onSuccess: async (result) => {
      result.items.forEach((book) => {
        client.setQueryData(queryKeys.books.detail(book.id), book);
      });
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.books.all() }),
        client.invalidateQueries({ queryKey: queryKeys.organization.all() }),
        client.invalidateQueries({ queryKey: queryKeys.stats.all() }),
        client.invalidateQueries({ queryKey: queryKeys.goals.all() }),
        client.invalidateQueries({ queryKey: queryKeys.books.undoHistory() }),
      ]);
    },
  });
}

export function useUndoHistoryQuery() {
  return useQuery({
    queryKey: queryKeys.books.undoHistory(),
    queryFn: ({ signal }) => bookService.getUndoHistory(signal),
    staleTime: 10_000,
  });
}

export function useTodaySessionsQuery() {
  return useQuery({
    queryKey: queryKeys.sessions.today(),
    queryFn: ({ signal }) => readingSessionService.getTodayReadingSessions(signal),
    staleTime: BOOK_STALE_TIME,
  });
}

export function useSessionsQuery(params: SessionListParams = {}) {
  return useQuery({
    queryKey: queryKeys.sessions.list(params),
    queryFn: ({ signal }) =>
      readingSessionService.getAllReadingSessions(params, signal),
    staleTime: BOOK_STALE_TIME,
  });
}

export function useCreateSessionMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (session: ReadingSessionCreate) =>
      readingSessionService.createReadingSession(session),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.sessions.all() }),
        client.invalidateQueries({ queryKey: queryKeys.journal.all() }),
        client.invalidateQueries({ queryKey: queryKeys.stats.all() }),
        client.invalidateQueries({ queryKey: queryKeys.goals.all() }),
      ]);
    },
  });
}

export function useDeleteSessionMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (session: Pick<ReadingSession, "id" | "updated_at">) =>
      readingSessionService.deleteReadingSession(session.id, session.updated_at),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.sessions.all() }),
        client.invalidateQueries({ queryKey: queryKeys.stats.all() }),
        client.invalidateQueries({ queryKey: queryKeys.goals.all() }),
        client.invalidateQueries({ queryKey: queryKeys.books.all() }),
        client.invalidateQueries({ queryKey: queryKeys.journal.all() }),
      ]);
    },
  });
}

export function useUpdateSessionMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      session,
      updates,
    }: {
      session: ReadingSession;
      updates: ReadingSessionUpdate;
    }) =>
      readingSessionService.updateReadingSession(
        session.id,
        updates,
        session.updated_at
      ),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.sessions.all() }),
        client.invalidateQueries({ queryKey: queryKeys.stats.all() }),
        client.invalidateQueries({ queryKey: queryKeys.goals.all() }),
        client.invalidateQueries({ queryKey: queryKeys.books.all() }),
        client.invalidateQueries({ queryKey: queryKeys.journal.all() }),
      ]);
    },
  });
}

export function useBookSessionsQuery(bookId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.sessions.book(bookId ?? 0),
    queryFn: ({ signal }) =>
      readingSessionService.getBookReadingSessions(bookId as number, signal),
    enabled: bookId !== undefined && Number.isInteger(bookId) && bookId > 0,
    staleTime: BOOK_STALE_TIME,
  });
}

export function useBookForecastQuery(bookId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.sessions.forecast(bookId ?? 0),
    queryFn: ({ signal }) =>
      readingSessionService.getBookReadingForecast(bookId as number, signal),
    enabled: bookId !== undefined && Number.isInteger(bookId) && bookId > 0,
    staleTime: BOOK_STALE_TIME,
  });
}

export function useBookReadingSummaryQuery(bookId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.books.readingSummary(bookId ?? 0),
    queryFn: ({ signal }) => bookService.getReadingSummary(bookId as number, signal),
    enabled: bookId !== undefined && Number.isInteger(bookId) && bookId > 0,
    staleTime: BOOK_STALE_TIME,
  });
}

export function useReadingCyclesQuery(bookId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.readingCycles.entry(bookId ?? 0),
    queryFn: ({ signal }) => readingCycleService.getHistory(bookId as number, signal),
    enabled: bookId !== undefined && Number.isInteger(bookId) && bookId > 0,
    staleTime: BOOK_STALE_TIME,
  });
}

export function useStartRereadMutation(bookId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (expectedOrdinal?: number) =>
      readingCycleService.startReread(bookId, expectedOrdinal),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.readingCycles.entry(bookId) }),
        client.invalidateQueries({ queryKey: queryKeys.books.detail(bookId) }),
        client.invalidateQueries({ queryKey: queryKeys.books.editionProgress(bookId) }),
        client.invalidateQueries({ queryKey: queryKeys.books.lists() }),
        client.invalidateQueries({ queryKey: queryKeys.stats.all() }),
      ]);
    },
  });
}

export function useJournalQuery(filters: JournalFilters = {}, enabled = true) {
  return useQuery({
    queryKey: queryKeys.journal.list(filters),
    queryFn: ({ signal }) => journalService.list(filters, signal),
    staleTime: BOOK_STALE_TIME,
    enabled,
  });
}

export function useReflectionPromptsQuery() {
  return useQuery({
    queryKey: queryKeys.journal.reflectionPrompts(),
    queryFn: ({ signal }) => journalService.listReflectionPrompts(signal),
    staleTime: BOOK_STALE_TIME,
  });
}

export function useDismissReflectionPromptMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (prompt: ReflectionPrompt) =>
      journalService.dismissReflectionPrompt(prompt),
    onSuccess: async () => {
      await client.invalidateQueries({
        queryKey: queryKeys.journal.reflectionPrompts(),
      });
    },
  });
}

export function useReflectionHistoryQuery(entryId: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.journal.reflectionHistory(entryId),
    queryFn: () => journalService.getReflectionHistory(entryId),
    enabled,
  });
}

export function useCreateJournalEntryMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entry: JournalEntryCreate) => journalService.create(entry),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.journal.all() }),
        client.invalidateQueries({
          queryKey: queryKeys.journal.reflectionPrompts(),
        }),
      ]);
    },
  });
}

export function useUpdateJournalEntryMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: JournalEntryUpdate }) => {
      const entry = client
        .getQueriesData<JournalEntryList>({ queryKey: queryKeys.journal.all() })
        .flatMap(([, page]) => page?.items ?? [])
        .find((item) => item.id === id);
      return journalService.update(id, updates, entry?.updated_at);
    },
    onSuccess: async (entry: JournalEntry) => {
      await client.invalidateQueries({ queryKey: queryKeys.journal.all() });
      return entry;
    },
  });
}

export function useDeleteJournalEntryMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => {
      const entry = client
        .getQueriesData<JournalEntryList>({ queryKey: queryKeys.journal.all() })
        .flatMap(([, page]) => page?.items ?? [])
        .find((item) => item.id === id);
      return journalService.delete(id, entry?.updated_at);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.journal.all() });
    },
  });
}

export function useBulkOrganizeHighlightsMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: journalService.bulkOrganize,
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.journal.all() });
    },
  });
}

export function useUserStatsQuery() {
  return useQuery({
    queryKey: queryKeys.stats.user(),
    queryFn: ({ signal }) => statsService.getUserStats(signal),
    staleTime: STATS_STALE_TIME,
  });
}

export function useReadingHabitsQuery(days: number) {
  return useQuery({
    queryKey: queryKeys.stats.habits(days),
    queryFn: ({ signal }) => statsService.getReadingHabits(days, signal),
    staleTime: STATS_STALE_TIME,
  });
}
export function useMonthlySummaryQuery(year?: number) {
  return useQuery({
    queryKey: queryKeys.stats.monthly(year),
    queryFn: ({ signal }) => statsService.getMonthlySummary(year, signal),
    staleTime: STATS_STALE_TIME,
  });
}
export function useBookProgressQuery(bookId?: number) {
  return useQuery({
    queryKey: queryKeys.stats.bookProgress(bookId),
    queryFn: ({ signal }) => statsService.getBookProgress(bookId, signal),
    staleTime: STATS_STALE_TIME,
  });
}
export function useWeeklyGoalsQuery() {
  return useQuery({
    queryKey: queryKeys.stats.weeklyGoals(),
    queryFn: ({ signal }) => statsService.getWeeklyGoals(signal),
    staleTime: STATS_STALE_TIME,
  });
}

export function useReadingGoalsQuery() {
  return useQuery({
    queryKey: queryKeys.goals.list(),
    queryFn: ({ signal }) => readingGoalService.list(signal),
    staleTime: STATS_STALE_TIME,
  });
}

export function useCreateReadingGoalMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (goal: ReadingGoalCreate) => readingGoalService.create(goal),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.goals.all() });
    },
  });
}

export function useUpdateReadingGoalMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      goalId,
      payload,
    }: {
      goalId: number;
      payload: ReadingGoalUpdate;
    }) => {
      const goal = client
        .getQueryData<ReadingGoal[]>(queryKeys.goals.list())
        ?.find((item) => item.id === goalId);
      return readingGoalService.update(goalId, payload, goal?.updated_at);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.goals.all() });
    },
  });
}

export function useDeleteReadingGoalMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (goalId: number) => {
      const goal = client
        .getQueryData<ReadingGoal[]>(queryKeys.goals.list())
        ?.find((item) => item.id === goalId);
      return readingGoalService.remove(goalId, goal?.updated_at);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.goals.all() });
    },
  });
}

export function useCollectionsQuery() {
  return useQuery({
    queryKey: queryKeys.organization.collections(),
    queryFn: ({ signal }) => libraryOrganizationService.listCollections(signal),
    staleTime: BOOK_STALE_TIME,
  });
}

export function useTagsQuery() {
  return useQuery({
    queryKey: queryKeys.organization.tags(),
    queryFn: ({ signal }) => libraryOrganizationService.listTags(signal),
    staleTime: BOOK_STALE_TIME,
  });
}

export function useLocationsQuery() {
  return useQuery({
    queryKey: queryKeys.locations.list(),
    queryFn: ({ signal }) => libraryLocationService.list(signal),
    staleTime: BOOK_STALE_TIME,
  });
}

export function useCreateLocationMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: LibraryLocationInput) =>
      libraryLocationService.create(payload),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.locations.all() });
    },
  });
}

export function useUpdateLocationMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      location,
      payload,
    }: {
      location: LibraryLocation;
      payload: Partial<LibraryLocationInput>;
    }) => libraryLocationService.update(location, payload),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.locations.all() }),
        client.invalidateQueries({ queryKey: queryKeys.books.all() }),
      ]);
    },
  });
}

export function useDeleteLocationMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (location: LibraryLocation) => libraryLocationService.remove(location),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.locations.all() }),
        client.invalidateQueries({ queryKey: queryKeys.books.all() }),
      ]);
    },
  });
}

export function useSavedLibraryViewsQuery() {
  return useQuery({
    queryKey: queryKeys.libraryViews.list(),
    queryFn: ({ signal }) => libraryViewService.list(signal),
    staleTime: BOOK_STALE_TIME,
  });
}

export function useCreateSavedLibraryViewMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: SavedLibraryViewCreate) => libraryViewService.create(payload),
    onSuccess: async () => {
      await client.invalidateQueries({
        queryKey: queryKeys.libraryViews.all(),
      });
    },
  });
}

export function useUpdateSavedLibraryViewMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: SavedLibraryViewUpdate }) => {
      const view = client
        .getQueryData<SavedLibraryView[]>(queryKeys.libraryViews.list())
        ?.find((item) => item.id === id);
      return libraryViewService.update(id, payload, view?.updated_at);
    },
    onSuccess: async () => {
      await client.invalidateQueries({
        queryKey: queryKeys.libraryViews.all(),
      });
    },
  });
}

export function useDeleteSavedLibraryViewMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => {
      const view = client
        .getQueryData<SavedLibraryView[]>(queryKeys.libraryViews.list())
        ?.find((item) => item.id === id);
      return libraryViewService.remove(id, view?.updated_at);
    },
    onSuccess: async () => {
      await client.invalidateQueries({
        queryKey: queryKeys.libraryViews.all(),
      });
    },
  });
}

export function useCreateCollectionMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: CollectionCreate) =>
      libraryOrganizationService.createCollection(payload),
    onSuccess: async () => {
      await client.invalidateQueries({
        queryKey: queryKeys.organization.all(),
      });
    },
  });
}

export function useDeleteCollectionMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => {
      const collection = client
        .getQueryData<LibraryCollection[]>(queryKeys.organization.collections())
        ?.find((item) => item.id === id);
      return libraryOrganizationService.deleteCollection(id, collection?.updated_at);
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.organization.all() }),
        client.invalidateQueries({ queryKey: queryKeys.books.all() }),
      ]);
    },
  });
}

export function useCreateTagMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: TagCreate) => libraryOrganizationService.createTag(payload),
    onSuccess: async () => {
      await client.invalidateQueries({
        queryKey: queryKeys.organization.all(),
      });
    },
  });
}

export function useDeleteTagMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => {
      const tag = client
        .getQueryData<LibraryTag[]>(queryKeys.organization.tags())
        ?.find((item) => item.id === id);
      return libraryOrganizationService.deleteTag(id, tag?.updated_at);
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.organization.all() }),
        client.invalidateQueries({ queryKey: queryKeys.books.all() }),
      ]);
    },
  });
}

export function useUpdateEntryOrganizationMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookId,
      payload,
    }: {
      bookId: number;
      payload: EntryOrganizationUpdate;
    }) => {
      const detail = client.getQueryData<Book>(queryKeys.books.detail(bookId));
      const listBook = client
        .getQueriesData<BookPage>({ queryKey: queryKeys.books.lists() })
        .flatMap(([, page]) => page?.items ?? [])
        .find((book) => book.id === bookId);
      return libraryOrganizationService.updateEntry(
        bookId,
        payload,
        detail?.updated_at ?? listBook?.updated_at
      );
    },
    onSuccess: async (book: Book) => {
      client.setQueryData(queryKeys.books.detail(book.id), book);
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.books.lists() }),
        client.invalidateQueries({ queryKey: queryKeys.organization.all() }),
      ]);
    },
  });
}

export function useCreateBookMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (book: BookCreate) => bookService.createBook(book),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.books.lists() }),
        client.invalidateQueries({ queryKey: queryKeys.stats.user() }),
      ]);
    },
  });
}

export function useUpdateBookMutation(bookId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (book: BookUpdate) =>
      bookService.updateBook(
        bookId,
        book,
        client.getQueryData<Book>(queryKeys.books.detail(bookId))?.updated_at
      ),
    onSuccess: async (book: Book) => {
      client.setQueryData(queryKeys.books.detail(bookId), book);
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.books.lists() }),
        client.invalidateQueries({ queryKey: queryKeys.books.editionProgress(bookId) }),
        client.invalidateQueries({ queryKey: queryKeys.stats.user() }),
      ]);
    },
  });
}

export function useUpdateBookStatusMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookId,
      readingStatus,
    }: {
      bookId: number;
      readingStatus: Book["reading_status"];
    }) => {
      const detail = client.getQueryData<Book>(queryKeys.books.detail(bookId));
      const listBook = client
        .getQueriesData<BookPage>({ queryKey: queryKeys.books.lists() })
        .flatMap(([, page]) => page?.items ?? [])
        .find((book) => book.id === bookId);
      return bookService.updateBook(
        bookId,
        { reading_status: readingStatus },
        detail?.updated_at ?? listBook?.updated_at
      );
    },
    onSuccess: async (book: Book) => {
      client.setQueryData(queryKeys.books.detail(book.id), book);
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.books.lists() }),
        client.invalidateQueries({ queryKey: queryKeys.stats.all() }),
        client.invalidateQueries({ queryKey: queryKeys.goals.all() }),
      ]);
    },
  });
}

export function useAbandonBookMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ book, data }: { book: Book; data: BookAbandon }) =>
      bookService.abandonBook(book.id, data, book.updated_at),
    onSuccess: async (book: Book) => {
      client.setQueryData(queryKeys.books.detail(book.id), book);
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.books.lists() }),
        client.invalidateQueries({ queryKey: queryKeys.stats.all() }),
        client.invalidateQueries({ queryKey: queryKeys.goals.all() }),
        client.invalidateQueries({ queryKey: queryKeys.recommendations.all() }),
      ]);
    },
  });
}

export function useResumeBookMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ book, data }: { book: Book; data: BookResume }) =>
      bookService.resumeBook(book.id, data, book.updated_at),
    onSuccess: async (book: Book) => {
      client.setQueryData(queryKeys.books.detail(book.id), book);
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.books.lists() }),
        client.invalidateQueries({ queryKey: queryKeys.stats.all() }),
        client.invalidateQueries({ queryKey: queryKeys.goals.all() }),
        client.invalidateQueries({ queryKey: queryKeys.recommendations.all() }),
      ]);
    },
  });
}

export function useDeleteBookMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (bookId: number) => {
      const detail = client.getQueryData<Book>(queryKeys.books.detail(bookId));
      const listBook = client
        .getQueriesData<BookPage>({ queryKey: queryKeys.books.lists() })
        .flatMap(([, page]) => page?.items ?? [])
        .find((book) => book.id === bookId);
      return bookService.deleteBook(bookId, detail?.updated_at ?? listBook?.updated_at);
    },
    onSuccess: async (_data, bookId: number) => {
      client.removeQueries({ queryKey: queryKeys.books.detail(bookId) });
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.books.lists() }),
        client.invalidateQueries({
          queryKey: queryKeys.books.editionProgress(bookId),
        }),
        client.invalidateQueries({ queryKey: queryKeys.sessions.all() }),
        client.invalidateQueries({ queryKey: queryKeys.journal.all() }),
        client.invalidateQueries({ queryKey: queryKeys.stats.all() }),
        client.invalidateQueries({ queryKey: queryKeys.goals.all() }),
        client.invalidateQueries({ queryKey: queryKeys.books.undoHistory() }),
      ]);
    },
  });
}

export function useReadingProgressMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (progress: ReadingProgressCreate) =>
      readingProgressService.create(progress),
    onSuccess: async (progress: ReadingProgress) => {
      const applyProgress = (book: Book): Book =>
        book.id === progress.library_entry_id
          ? {
              ...book,
              current_page: progress.ending_page,
              reading_status: progress.reading_status,
              last_read_at: progress.last_read_at,
            }
          : book;
      client.setQueryData<Book | undefined>(
        queryKeys.books.detail(progress.library_entry_id),
        (book: Book | undefined) => (book ? applyProgress(book) : book)
      );
      client.setQueriesData<BookPage>(
        { queryKey: queryKeys.books.lists() },
        (page: BookPage | undefined) =>
          page ? { ...page, items: page.items.map(applyProgress) } : page
      );
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.books.lists() }),
        client.invalidateQueries({
          queryKey: queryKeys.books.editionProgress(progress.library_entry_id),
        }),
        client.invalidateQueries({ queryKey: queryKeys.sessions.all() }),
        client.invalidateQueries({ queryKey: queryKeys.stats.all() }),
        client.invalidateQueries({ queryKey: queryKeys.goals.all() }),
      ]);
    },
  });
}
